import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectRes, allProjectsRes] = await Promise.all([
          axios.get(`${API}/projects/${id}`),
          axios.get(`${API}/projects`)
        ]);
        setProject(projectRes.data);
        setAllProjects(allProjectsRes.data);
        setCurrentImageIndex(0);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const galleryImages = project?.gallery?.length > 0 
    ? project.gallery 
    : [project?.image_url];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen" data-testid="project-detail-loading">
        <div className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-stone-100 w-1/4" />
            <div className="h-12 bg-stone-100 w-3/4" />
            <div className="aspect-[16/9] bg-stone-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pt-20 min-h-screen" data-testid="project-not-found">
        <div className="py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-manrope text-3xl font-light text-stone-900 mb-4">
            Project not found
          </h1>
          <Link
            to="/projects"
            className="font-dm-sans text-stone-500 hover:text-stone-900 transition-colors duration-300"
          >
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Find next and previous projects
  const currentIndex = allProjects.findIndex(p => p.id === id);
  const nextProject = allProjects[currentIndex + 1] || allProjects[0];
  const prevProject = allProjects[currentIndex - 1] || allProjects[allProjects.length - 1];

  return (
    <div data-testid="project-detail-page" className="pt-20">
      {/* Back Link */}
      <div className="px-6 md:px-12 lg:px-24 pt-8">
        <Link
          to="/projects"
          data-testid="back-to-projects"
          className="inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-500 hover:text-stone-900 transition-colors duration-300"
        >
          <ArrowLeft size={14} />
          Back to Projects
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24" data-testid="project-hero">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mb-12"
        >
          <span className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block">
            {project.category} · {project.year}
          </span>
          <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 leading-tight mb-6">
            {project.title}
          </h1>
          <p className="font-dm-sans text-lg text-stone-500">
            {project.location}
          </p>
        </motion.div>

        {/* Main Image / Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="overflow-hidden bg-stone-100 relative flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImageIndex}
                src={galleryImages[currentImageIndex]}
                alt={`${project.title} - Image ${currentImageIndex + 1}`}
                className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            </AnimatePresence>

            {/* Gallery Navigation */}
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-900 hover:bg-white transition-colors duration-300"
                  aria-label="Previous image"
                  data-testid="gallery-prev"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-900 hover:bg-white transition-colors duration-300"
                  aria-label="Next image"
                  data-testid="gallery-next"
                >
                  <ChevronRight size={24} />
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="font-dm-sans text-sm text-stone-900">
                    {currentImageIndex + 1} / {galleryImages.length}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {galleryImages.length > 1 && (
            <div className="flex gap-3 mt-4 overflow-x-auto pb-2 justify-center">
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 h-16 overflow-hidden rounded transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'ring-2 ring-stone-900 opacity-100' 
                      : 'opacity-50 hover:opacity-80'
                  }`}
                  data-testid={`thumbnail-${index}`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="h-full w-auto object-contain"
                  />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </section>

      {/* Project Description */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="project-description">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-4"
          >
            <h2 className="font-manrope text-2xl font-light tracking-tight text-stone-900 mb-8">
              Project Overview
            </h2>
            <div className="space-y-6">
              <div>
                <span className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-2">
                  Location
                </span>
                <p className="font-dm-sans text-stone-900">{project.location}</p>
              </div>
              <div>
                <span className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-2">
                  Category
                </span>
                <p className="font-dm-sans text-stone-900">{project.category}</p>
              </div>
              <div>
                <span className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-2">
                  Year
                </span>
                <p className="font-dm-sans text-stone-900">{project.year}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-8"
          >
            <p className="font-dm-sans text-lg text-stone-600 leading-relaxed mb-8">
              {project.description}
            </p>
            <p className="font-dm-sans text-stone-500 leading-relaxed">
              This project exemplifies our commitment to creating spaces that respond to their context while meeting the unique needs of our clients. Through careful consideration of materials, light, and spatial flow, we've crafted an environment that feels both timeless and contemporary.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Project Navigation */}
      <section className="py-16 md:py-24 border-t border-stone-200" data-testid="project-navigation">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 gap-8">
            <Link
              to={`/projects/${prevProject.id}`}
              data-testid="prev-project"
              className="group"
            >
              <span className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide mb-2 block">
                Previous
              </span>
              <div className="flex items-center gap-3">
                <ArrowLeft size={16} className="text-stone-400 group-hover:text-stone-900 transition-colors duration-300" />
                <span className="font-manrope text-lg font-light text-stone-900 group-hover:text-stone-600 transition-colors duration-300">
                  {prevProject.title}
                </span>
              </div>
            </Link>

            <Link
              to={`/projects/${nextProject.id}`}
              data-testid="next-project"
              className="group text-right"
            >
              <span className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide mb-2 block">
                Next
              </span>
              <div className="flex items-center justify-end gap-3">
                <span className="font-manrope text-lg font-light text-stone-900 group-hover:text-stone-600 transition-colors duration-300">
                  {nextProject.title}
                </span>
                <ArrowRight size={16} className="text-stone-400 group-hover:text-stone-900 transition-colors duration-300" />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
